import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCorrespondence, useMarkProcessed, useManualMatch } from '../../hooks/useCorrespondence';
import ManualMatchModal from './ManualMatchModal';
import './Correspondence.css';

/**
 * Correspondence Component
 * Shows all incoming IP Office correspondence
 * Supports ?id={correspondenceId} URL param to auto-open a specific item
 */
function Correspondence() {
  const { t } = useTranslation('attorney');
  const [filter, setFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const highlightedRef = useRef(null);
  const initialLoadDone = useRef(false);

  // Read URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    if (id) {
      setHighlightedId(id);
      setFilter('all'); // Show all to ensure we can find it
    }
  }, []);

  const { data, isLoading, error, refetch } = useCorrespondence({
    status: filter === 'all' ? undefined : filter
  });
  const markProcessedMutation = useMarkProcessed();
  const manualMatchMutation = useManualMatch();
  const [showManualMatch, setShowManualMatch] = useState(false);

  const correspondence = data?.correspondence || data || [];

  // Auto-open and scroll to highlighted item when data loads
  useEffect(() => {
    if (highlightedId && correspondence.length > 0 && !initialLoadDone.current) {
      const item = correspondence.find(c => c.id === highlightedId);
      if (item) {
        setSelectedItem(item);
        initialLoadDone.current = true;
      }
      // Scroll to highlighted card
      if (highlightedRef.current) {
        highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedId, correspondence]);

  const handleMarkProcessed = async (id, notes) => {
    try {
      await markProcessedMutation.mutateAsync({ correspondenceId: id, notes });
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to mark as processed:', err);
    }
  };

  const handleManualMatch = async (matchData) => {
    if (!selectedItem) return;

    try {
      await manualMatchMutation.mutateAsync({
        correspondenceId: selectedItem.id,
        ...matchData
      });
      setShowManualMatch(false);
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to manual match:', err);
      alert(t('attorney.manualMatch.errors.saveFailed', 'Failed to save manual match. Please try again.'));
    }
  };

  const getClassificationIcon = (classification) => {
    switch (classification) {
      case 'office_action': return '⚠️';
      case 'acceptance': return '✅';
      case 'opposition': return '⚔️';
      case 'renewal_notice': return '🔄';
      case 'registration': return '🎉';
      default: return '📧';
    }
  };

  const getClassificationLabel = (classification) => {
    switch (classification) {
      case 'office_action': return t('attorney.correspondence.types.officeAction', 'Office Action');
      case 'acceptance': return t('attorney.correspondence.types.acceptance', 'Acceptance');
      case 'opposition': return t('attorney.correspondence.types.opposition', 'Opposition');
      case 'renewal_notice': return t('attorney.correspondence.types.renewal', 'Renewal Notice');
      case 'registration': return t('attorney.correspondence.types.registration', 'Registration');
      default: return t('attorney.correspondence.types.general', 'General');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="correspondence-loading">
        <div className="loading-spinner" />
        <p>{t('attorney.correspondence.loading', 'Loading correspondence...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="correspondence-error">
        <p>{t('attorney.correspondence.error', 'Failed to load correspondence')}</p>
        <button onClick={() => refetch()}>
          {t('attorney.correspondence.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="correspondence-container">
      <div className="correspondence-header">
        <h1>{t('attorney.correspondence.title', 'Correspondence')}</h1>
        <div className="correspondence-controls">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('attorney.correspondence.filter.all', 'All')}</option>
            <option value="unprocessed">{t('attorney.correspondence.filter.unprocessed', 'Unprocessed')}</option>
            <option value="needs_manual_matching">{t('attorney.correspondence.filter.needsMatching', 'Needs Matching')}</option>
            <option value="processed">{t('attorney.correspondence.filter.processed', 'Processed')}</option>
          </select>
          <button onClick={() => refetch()} className="refresh-btn">
            {t('attorney.correspondence.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {correspondence.length === 0 ? (
        <div className="correspondence-empty">
          <div className="empty-icon">📭</div>
          <p>{t('attorney.correspondence.empty', 'No correspondence found')}</p>
          <p className="empty-hint">
            {t('attorney.correspondence.emptyHint', 'Correspondence will appear here when emails are synced from the docketing mailbox.')}
          </p>
        </div>
      ) : (
        <div className="correspondence-list">
          {correspondence.map((item) => {
            const isHighlighted = item.id === highlightedId;
            return (
            <div
              key={item.id}
              ref={isHighlighted ? highlightedRef : null}
              className={`correspondence-card ${item.status === 'processed' ? 'processed' : ''} ${isHighlighted ? 'highlighted' : ''}`}
              onClick={() => setSelectedItem(item)}
            >
              <div className="correspondence-icon">
                {getClassificationIcon(item.classification)}
              </div>

              <div className="correspondence-content">
                <div className="correspondence-row">
                  <span className="correspondence-subject">{item.subject || 'No Subject'}</span>
                  <span className="correspondence-date">{formatDate(item.receivedAt)}</span>
                </div>

                <div className="correspondence-row">
                  <span className="correspondence-from">{item.from}</span>
                  <span className={`correspondence-classification ${item.classification}`}>
                    {getClassificationLabel(item.classification)}
                  </span>
                </div>

                <div className="correspondence-row">
                  {item.applicationId ? (
                    <span className="correspondence-linked">
                      Linked: {item.applicationId}
                    </span>
                  ) : (
                    <span className="correspondence-unlinked">
                      {t('attorney.correspondence.notLinked', 'Not linked to application')}
                    </span>
                  )}
                  {item.jurisdiction && (
                    <span className="correspondence-jurisdiction">{item.jurisdiction}</span>
                  )}
                </div>

                {item.summary && (
                  <p className="correspondence-summary">{item.summary}</p>
                )}
              </div>

              <div className="correspondence-status">
                {item.status === 'processed' ? (
                  <span className="status-badge processed">Processed</span>
                ) : item.status === 'needs_manual_matching' ? (
                  <span className="status-badge needs-matching">Needs Matching</span>
                ) : (
                  <span className="status-badge unprocessed">Unprocessed</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedItem.subject || 'No Subject'}</h2>
              <button className="modal-close" onClick={() => setSelectedItem(null)}>×</button>
            </div>

            <div className="modal-meta">
              <div className="meta-row">
                <span className="meta-label">{t('attorney.correspondence.from', 'From')}:</span>
                <span className="meta-value">{selectedItem.from}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">{t('attorney.correspondence.received', 'Received')}:</span>
                <span className="meta-value">{formatDate(selectedItem.receivedAt)}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">{t('attorney.correspondence.classification', 'Classification')}:</span>
                <span className="meta-value">{getClassificationLabel(selectedItem.classification)}</span>
              </div>
              {selectedItem.jurisdiction && (
                <div className="meta-row">
                  <span className="meta-label">{t('attorney.correspondence.jurisdiction', 'Jurisdiction')}:</span>
                  <span className="meta-value">{selectedItem.jurisdiction}</span>
                </div>
              )}
              {selectedItem.applicationId && (
                <div className="meta-row">
                  <span className="meta-label">{t('attorney.correspondence.application', 'Application')}:</span>
                  <span className="meta-value">{selectedItem.applicationId}</span>
                </div>
              )}
            </div>

            {selectedItem.summary && (
              <div className="modal-section">
                <h3>{t('attorney.correspondence.summary', 'Summary')}</h3>
                <p>{selectedItem.summary}</p>
              </div>
            )}

            <div className="modal-section">
              <h3>{t('attorney.correspondence.body', 'Email Body')}</h3>
              <div className="email-body">
                {selectedItem.bodyText || 'No content available'}
              </div>
            </div>

            {selectedItem.status !== 'processed' && (
              <div className="modal-actions">
                {!selectedItem.applicationId && (
                  <button
                    className="btn-link"
                    onClick={() => setShowManualMatch(true)}
                  >
                    {t('attorney.correspondence.manualMatch', 'Manual Match')}
                  </button>
                )}
                <button
                  className="btn-process"
                  onClick={() => handleMarkProcessed(selectedItem.id, '')}
                  disabled={markProcessedMutation.isPending}
                >
                  {markProcessedMutation.isPending
                    ? t('attorney.correspondence.processing', 'Processing...')
                    : t('attorney.correspondence.markProcessed', 'Mark as Processed')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Match Modal */}
      {showManualMatch && selectedItem && (
        <ManualMatchModal
          correspondence={selectedItem}
          onClose={() => setShowManualMatch(false)}
          onSubmit={handleManualMatch}
          isSubmitting={manualMatchMutation.isPending}
        />
      )}
    </div>
  );
}

export default Correspondence;
